import React, { useState, useEffect } from "react";
import _ from "lodash";
import { _getBaseUrl, _slugify } from "../utils/helper";
import api from "../utils/api";
import StateView from "../utils/StateView";

const INITIAL_VALUES = {
  keywords: [],
  actueel_wijken: [],
  transitieagenda: [],
  nodeStatus: [true],
  page: 1,
  orderBy: "changed",
  postsPerPage: 99999,
  publicView: true,
};

const InspiratiepuntenFilter = () => {
  const [wijkFilterCollapse, setWijkFilterCollapse] = useState(true);
  const [transitieagendaFilterCollapse, setTransitieagendaFilterCollapse] = useState(true);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const container = document.querySelector("#inspiratiepunten-filter-container");
  const mode = container.getAttribute("data-view-mode");

  // --- Fetch filter options + restore filters from URL ---
  useEffect(() => {
    sessionStorage.removeItem("transitieagenda-scroll-pos");

    api
      .get("/inspiratiepunten_filteroptions")
      .then((response) => {
        setOptions({
          actueel_wijken: response.data.options.actueel_wijken,
          transitieagenda: response.data.options.transitieagenda,
        });
        setValues({
          ...values,
          transitieagenda: (response.data.options.transitieagenda || []).map(opt => opt.value),
        });
      })
      .catch(console.error);

    // Read filters from URL
    const params = new URLSearchParams(window.location.search);
    const filtersFromUrl = {
      keywords: params.getAll("keywords") || [],
      actueel_wijken: params.getAll("actueel_wijken") || [],
      transitieagenda: params.getAll("transitieagenda") || [],
    };

    const hasFilters =
      (filtersFromUrl.keywords && filtersFromUrl.keywords.length > 0) ||
      (filtersFromUrl.actueel_wijken && filtersFromUrl.actueel_wijken.length > 0) ||
      (filtersFromUrl.transitieagenda && filtersFromUrl.transitieagenda.length > 0);

    if (hasFilters) {
      setValues((prev) => ({ ...prev, ...filtersFromUrl }));
      document.dispatchEvent(
        new CustomEvent("filtersApplied", { detail: filtersFromUrl })
      );
    }
  }, []);

  const getFilterPlaceholder = (selectedValues, options) => {
    if (!selectedValues || selectedValues.length === 0) return "Alles";

    const labels = selectedValues
      .map((val) => {
        const match = options.find((opt) => opt.value === val);
        return match ? match.label : null;
      })
      .filter(Boolean);

    const result = labels.join(", ");
    if (result.length > 25) return result.slice(0, 25).replace(/,?\s*$/, "") + "...";
    return result;
  };

  const FilterCollapsibleBox = ({
    children,
    collapse,
    selectedValues,
    options,
    setCollapse,
    collapseOther,
    selectedCount,
  }) => (
    <div className="rounded-3xl bg-transparent border border-gray-500 px-6">
      <div
        className="filter-label-wrapper flex justify-between items-center hover:opacity-70 cursor-pointer"
        style={{ height: "50px" }}
        onClick={() => {
          setCollapse(!collapse);
          if (Array.isArray(collapseOther)) collapseOther.forEach((fn) => fn(true));
        }}
      >
        {selectedCount >= 0 ? (
          <span className="text-base"><span className="font-semibold">{selectedCount}</span> Thema's geselecteerd</span>
        ) : (
          <span className="text-base">{getFilterPlaceholder(selectedValues, options)}</span>
        )}

        <img
          src={_getBaseUrl("/src/assets/icons/arrow-down.svg", true)}
          alt="Icon"
          className={!collapse ? "rotate-180" : ""}
        />
      </div>
      {!collapse && (
        <div className={`pb-4 h-[calc(100vh-600px)] sm:h-fit min-h-[100px] sm:min-h-fit`}
        // style={{ height: "calc(100vh - 610px)" }}
        >
          <div className="h-full overflow-y-auto pr-4 sm:pr-0" id="transitieagenda-scroll-container">{children}</div>
        </div>
      )}
    </div>
  );

  // --- Apply filters ---
  const applyFilters = (newValues) => {
    const button = document.getElementById("button-filter-mobile");

    const display = window.getComputedStyle(button).display;

    if (display === "flex" || display === "block") {
      setTimeout(() => {
        button.click(); // Close mobile filter if open
      }, 100);
    }

    setLoading(true);

    try {
      // Start with existing query params
      const params = new URLSearchParams(window.location.search);

      // Clear out old filter values (but keep `view`)
      params.delete("keywords");
      params.delete("actueel_wijken");
      params.delete("transitieagenda");

      // Append new filter values
      (newValues.keywords || []).forEach((kw) => params.append("keywords", kw));
      (newValues.actueel_wijken || []).forEach((w) =>
        params.append("actueel_wijken", w)
      );
      (newValues.transitieagenda || []).forEach((t) =>
        params.append("transitieagenda", t)
      );

      // Build new URL (keeping ?view=map or ?view=list if it was there)
      const newUrl = `${location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    } catch (err) {
      console.error("Failed to save filters in URL:", err);
    }

    document.dispatchEvent(
      new CustomEvent("filtersApplied", { detail: newValues })
    );
  };


  // Stop loader when data fetch completes
  useEffect(() => {
    const handleCompleted = () => {
      setLoading(false);
      setWijkFilterCollapse(true);
      setTransitieagendaFilterCollapse(true);
    };
    document.addEventListener("fetchDataCompleted", handleCompleted);
    return () => document.removeEventListener("fetchDataCompleted", handleCompleted);
  }, []);

  // Restore scroll position when filters change
  useEffect(() => {
    const scrollContainer = document.getElementById("transitieagenda-scroll-container");
    if (!scrollContainer) return;

    // Restore scroll position
    const savedScroll = sessionStorage.getItem("transitieagenda-scroll-pos");
    if (savedScroll) {
      scrollContainer.scrollTop = parseInt(savedScroll, 10);
    }

    const handleScroll = () => {
      console.log("Saving scroll position:", scrollContainer.scrollTop);
      sessionStorage.setItem("transitieagenda-scroll-pos", scrollContainer.scrollTop);
    };
    
    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [values.transitieagenda, values.actueel_wijken]);

  // Store scroll position on scroll
  useEffect(() => {
    const scrollContainer = document.getElementById("transitieagenda-scroll-container");
    if (!scrollContainer) return;

    sessionStorage.removeItem("transitieagenda-scroll-pos");

    // Restore scroll position
    // const savedScroll = sessionStorage.getItem("transitieagenda-scroll-pos");
    // if (savedScroll) {
    //   console.log("Restoring scroll position:", savedScroll);
    //   scrollContainer.scrollTop = parseInt(savedScroll, 10);
    // }

    // Store scroll position on scroll
    const handleScroll = () => {
      console.log("Saving scroll position:", scrollContainer.scrollTop);
      sessionStorage.setItem("transitieagenda-scroll-pos", scrollContainer.scrollTop);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [transitieagendaFilterCollapse, wijkFilterCollapse]);

  return (
    <React.Fragment>
      <div className="relative">
        {/* Search */}
        <div className="font-sharpgrotesk font-semibold text-xl mb-2 text-cirBlack">Zoeken</div>
        <fieldset className="searchBar relative border-none">
          <legend className="sr-only">Zoeken</legend>
          <input
            type="text"
            id="searchQueryInput"
            tabindex="0"
            name="searchQueryInput"
            placeholder="Zoeken"
            aria-label="Zoeken"
            value={values.keywords[0] || ""}
            onChange={(e) => {
              const value = e.target.value;
              setValues({
                ...values,
                keywords: value ? [value] : [],
              });
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                const value = e.target.value;
                setValues({
                  ...values,
                  keywords: value ? [value] : [],
                });
                applyFilters({
                  ...values,
                  keywords: value ? [value] : [],
                });
              }
            }}
            className="searchQueryNetwork w-full dark:text-cirBlack dark:hover:text-white hover:text-cirBlack dark:focus:text-white focus:text-cirBlack transition duration-150 ease-in-out hover:bg-white focus:bg-white dark:hover:bg-black dark:focus:bg-black focus:outline-none focus:ring-0 active:bg-primary-700 text-base h-full px-6 pb-2 pt-2.5 border border-gray-500 rounded-full placeholder-black"
            style={{ height: "50px" }}
          />
          <button
            id="searchQuerySubmit"
            type="submit"
            name="searchQuerySubmit"
            aria-label="Zoeken"
            className="text-2xl min-w-[24px] min-h-[24px] flex items-center justify-center absolute top-0 right-4"
          >
            <img src={_getBaseUrl("/src/assets/icons/search-icon.svg", true)} alt="" className="w-4 h-4" />

          </button>
        </fieldset>

        {/* Wijk */}
        <div className="font-sharpgrotesk font-semibold text-xl mt-5 mb-2 text-cirBlack">Wijk</div>
        <div className="my-2">
          <FilterCollapsibleBox
            collapse={wijkFilterCollapse}
            selectedValues={values.actueel_wijken}
            options={options.actueel_wijken || []}
            setCollapse={setWijkFilterCollapse}
            collapseOther={[setTransitieagendaFilterCollapse]}
          >
            <div>
              {options.actueel_wijken &&
                options.actueel_wijken.map((wijk, index) => (
                  <div key={index} className="flex items-center gap-2 my-3">
                    <div className="custom-checkbox">
                      <input
                        type="checkbox"
                        value={wijk.value}
                        id={`checkbox_${index}_${_slugify(wijk.label)}`}
                        checked={values.actueel_wijken.includes(wijk.value)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setValues((prev) => ({
                            ...prev,
                            actueel_wijken: checked
                              ? [...prev.actueel_wijken, wijk.value]
                              : prev.actueel_wijken.filter((v) => v !== wijk.value),
                          }));
                        }}
                        className="checkbox-form"
                      />
                      <span className="checkmark"></span>
                    </div>

                    <label
                      htmlFor={`checkbox_${index}_${_slugify(wijk.label)}`}
                      className="text-base leading-none"
                    >
                      {wijk.label}
                    </label>
                  </div>
                ))}
            </div>
          </FilterCollapsibleBox>
        </div>

        {/* Transitieagenda */}
        <div className="font-sharpgrotesk font-semibold text-xl mt-5 mb-2 text-cirBlack">Thema</div>
        <div className="my-2">
          <FilterCollapsibleBox
            collapse={transitieagendaFilterCollapse}
            selectedValues={values.transitieagenda}
            options={options.transitieagenda || []}
            setCollapse={setTransitieagendaFilterCollapse}
            collapseOther={[setWijkFilterCollapse]}
            selectedCount={values.transitieagenda.length > 0 ? values.transitieagenda.length : 0}
          >
            <div className="pb-4 grid grid-cols-1 gap-2">
              {options.transitieagenda &&
                options.transitieagenda.map((agenda, index) => (
                  <button
                    key={index}
                    className="!h-10 !min-h-0 rounded-full leading-none px-5 hover:opacity-70 cursor-pointer border border-cirBlack !text-base"
                    onClick={() => {
                      const isSelected = values.transitieagenda.includes(agenda.value);
                      setValues((prev) => ({
                        ...prev,
                        transitieagenda: isSelected
                          ? prev.transitieagenda.filter((v) => v !== agenda.value)
                          : [...prev.transitieagenda, agenda.value],
                      }));
                    }}
                    style={
                      values.transitieagenda.includes(agenda.value)
                        ? {
                          borderColor: agenda.color,
                          backgroundColor: agenda.color,
                          color: "white",
                        }
                        : undefined
                    }
                  >
                    {agenda.label}
                  </button>
                ))}
            </div>
          </FilterCollapsibleBox>
        </div>

        {/* Actions */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <button
              className="flex text-base gap-5 py-2.5 px-6 rounded-full justify-center border border-cirBlack bg-cirBlack hover:bg-white text-white hover:text-cirBlack transition-colors duration-500 w-full"
              onClick={() => applyFilters(values)}
            >
              Filters toepassen
            </button>
          </div>
          <div>
            <button
              className="flex text-base gap-5 py-2.5 px-6 rounded-full justify-center bg-white border border-cirBlack hover:invert transition-all hover:border-white duration-500 w-full"
              onClick={() => {
                setValues({
                  ...INITIAL_VALUES,
                  transitieagenda: (options.transitieagenda || []).map(opt => opt.value),
                });
                setWijkFilterCollapse(true);
                setTransitieagendaFilterCollapse(true);
                // Preserve the 'view' param if it exists
                const params = new URLSearchParams(window.location.search);
                const view = params.get("view");

                const newParams = new URLSearchParams();
                if (view) {
                  newParams.set("view", view);
                }

                window.history.replaceState(null, "", `${location.pathname}?${newParams.toString()}`);

                document.dispatchEvent(
                  new CustomEvent("filtersApplied", { detail: INITIAL_VALUES })
                );
              }}
            >
              Resetten
            </button>
          </div>
        </div>

        {loading && (
          <div className="bg-white bg-opacity-90 absolute top-0 left-0 w-full h-full flex justify-center items-center">
            <img
              src={_getBaseUrl("src/assets/loading.gif", true)}
              alt="Loading"
              className="w-12 -translate-y-full"
            />
          </div>
        )}
      </div>

      {/* Debug */}
      {/* <StateView
        title="Opties"
        values={options}
        floating
        collapsed
        class="max-w-[80vw] right-48 z-[99999999]"
      />
      <StateView
        title="Thema"
        values={values.transitieagenda}
        floating
        collapsed
        class="max-w-[80vw] right-1/2 z-[99999999]"
      /> */}
    </React.Fragment>
  );
};

export default InspiratiepuntenFilter;
